import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DASH_TOOLTIP } from '../../dashboard-overview/components/tooltipStyle';
import { useReport } from '../hooks/useReport';

// 'YYYY-MM' -> 'Sep' / 'Sep 2025'
const monthLabel = (m, withYear = false) => {
  if (typeof m !== 'string' || !m.includes('-')) return m;
  const [y, mm] = m.split('-');
  const d = new Date(Number(y), Number(mm) - 1, 1);
  if (Number.isNaN(d.getTime())) return m;
  return d.toLocaleString('en-US', withYear ? { month: 'short', year: 'numeric' } : { month: 'short' });
};

/**
 * Onboarding trend for the report — TRUE counts (no bulk-import trimming, unlike
 * the dashboard trend), so it reflects everything that happened in the period.
 */
const ReportTrendChart = ({ filters }) => {
  const { data, loading } = useReport('/reports/mis/trend', filters);
  const rows = Array.isArray(data) ? data : [];

  // The card's height is set by its grid-row sibling (the donuts + Country
  // column), so a fixed 200px chart left a dead band underneath. Fill whatever
  // height the card actually has — measured ourselves and passed to recharts as
  // a number, since ResponsiveContainer's own height re-measure is unreliable.
  const areaRef = React.useRef(null);
  const [areaH, setAreaH] = React.useState(0);
  React.useEffect(() => {
    const el = areaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => setAreaH(el.getBoundingClientRect().height);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    // The sibling column's cards grow as their data arrives, which resizes this
    // card's grid row shortly after mount — settle on the final height even if
    // a ResizeObserver delivery is missed.
    const settle = setTimeout(measure, 600);
    return () => { ro.disconnect(); clearTimeout(settle); };
  }, []);
  const chartH = Math.max(200, Math.floor(areaH));

  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 flex items-center justify-between border-b border-dash-border pb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-dash-foreground">Onboarding Trend</h3>
        <span className="text-[11px] text-dash-muted-foreground">true counts</span>
      </div>
      <div ref={areaRef} className="min-h-[200px] flex-1">
        {loading && rows.length === 0 ? (
          <div className="flex h-full items-center justify-center text-[11px] text-dash-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="flex h-full items-center justify-center text-[11px] text-dash-muted-foreground">No data available</div>
        ) : (
          <ResponsiveContainer width="100%" height={chartH}>
            <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="var(--dash-border)" vertical={false} />
              <XAxis
                dataKey="month"
                tickFormatter={(m) => monthLabel(m)}
                tickLine={false}
                axisLine={{ stroke: 'var(--dash-border)' }}
                tick={{ fontSize: 11, fill: 'var(--dash-muted-foreground)' }}
              />
              <YAxis
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                width={34}
                // "6,000" needs more than the 34px this axis gets; abbreviate so
                // the leading digits can't be clipped off ("6,000" -> "6k").
                tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k` : String(v))}
                tick={{ fontSize: 11, fill: 'var(--dash-muted-foreground)' }}
              />
              <Tooltip
                labelFormatter={(m) => monthLabel(m, true)}
                contentStyle={DASH_TOOLTIP.contentStyle}
                labelStyle={DASH_TOOLTIP.labelStyle}
                itemStyle={DASH_TOOLTIP.itemStyle}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="registered" stroke="var(--dash-chart-1)" strokeWidth={2} dot={{ r: 2 }} name="Registered" />
              <Line type="monotone" dataKey="approved" stroke="var(--dash-chart-2)" strokeWidth={2} dot={{ r: 2 }} name="Approved (Active)" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default ReportTrendChart;
